// components/main/ObjektDetail.jsx

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useAuth } from '../context/AuthContext.jsx';
import { useLoadAllData } from "../../hooks/useLoadAllData";
import Navbar from '../utils/Navbar';
import ObjektDetailForm from "./ObjektDetailForm";

const ObjektDetail = () => {
  const { fetchWithAuth, accessToken } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [objektData, setObjektData] = useState(null);
  const [formData, setFormData] = useState({});
  const { kategorien, ablageorte, typen, laender, materialien, waehrungen, zustaende, eigentuemer, loading, error } = useLoadAllData();
  const [selectedFiles, setSelectedFiles] = useState({
    bild01: null,
    bild02: null
  });

  const [loadingObj, setLoading] = useState(true);
  const [errorObj, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');

  // fetch objects
  useEffect(() => {
    if (!accessToken) return;

    const fetchObjekt = async () => {
      try {
        const response = await fetchWithAuth(`/api/main/${id}/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Fehler beim Laden der Objektdetails");
        }

        const data = await response.json();
        // console.log("GELADENE DATEN:", data);

        // optional check, if backend doesn't answer correct
        if (!data.id) {
          throw new Error("Ungültige Objekt-Antwort");
        }

        // console.log("GELADENE DATEN:", data);
        setObjektData(data);
        setFormData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchObjekt();
  }, [id, accessToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // convert date to ISOString, for correct transmission
    const isDate = value instanceof Date;
    setFormData((prev) => ({
      ...prev,
      // [name]: value
      [name]: isDate ? value.toISOString().split("T")[0] : value,
    }));
  };

  const handleImageChange = (name, file) => {
    setSelectedFiles(prev => ({ ...prev, [name]: file }));
  };

  const handleImageDelete = (key) => {
    setSelectedFiles((prev) => ({ ...prev, [key]: null, }));
    setFormData((prev) => ({ ...prev, [key]: null, }));
    setDeletedImages((prev) => ({ ...prev, [key]: true, }));
  };

  const [deletedImages, setDeletedImages] = useState({
    bild01: false,
    bild02: false,
  });

  const updateObjekt = async () => {
    try {
      const formPayload = new FormData();

      // debug
      // for (let pair of formPayload.entries()) {
      //   console.log(pair[0] + ', ' + pair[1]);
      // }

      for (const key in formData) {
        // if images -> no formData (strings)
        if ((key === "bild01" || key === "bild02")) continue;

        if (formData[key] !== null && formData[key] !== undefined) {
          formPayload.append(key, formData[key]);
        }
      }

      if (selectedFiles.bild01) {
        formPayload.append("bild01", selectedFiles.bild01);
      }
      if (selectedFiles.bild02) {
        formPayload.append("bild02", selectedFiles.bild02);
      }

      // send delete flags
      if (deletedImages.bild01) {
        formPayload.append("bild01_delete", "true");
      }
      if (deletedImages.bild02) {
        formPayload.append("bild02_delete", "true");
      }

      const response = await fetchWithAuth(`/api/main/${id}/update/`, {
        method: "PATCH",
        // now content type here, otherwise error because formData!
        body: formPayload,
      });

      if (!response.ok) {
        throw new Error("Fehler beim Aktualisieren");
      }

      navigate(`/objekte`);
    } catch (err) {
      setErrorMessage("Fehler: " + err.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Bist du sicher, dass du diese Münze löschen möchtest?")) return;

    try {
      const response = await fetchWithAuth(`/api/main/${id}/delete/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Fehler beim Löschen der Münze");
      }

      navigate("/objekte");
    } catch (err) {
      setErrorMessage("Löschen fehlgeschlagen: " + err.message);
    }
  };

  const handleCancelEdit = () => {
    setFormData(objektData);     // Reset form to original data
    setSelectedFiles({ bild01: null, bild02: null }); // Clear uploaded images
    setEditMode(false);          // Exit edit mode
  };

  if (loading) return <div>Lade Daten...</div>;
  if (loadingObj) return <p>Lade Details...</p>;

  if (error || errorObj) {
    const combinedError = error || errorObj?.message || 'Unbekannter Fehler';
    return <div data-testid="error">Fehler: {combinedError}</div>;
  }

  if (!formData || !formData.id) return <p>Lade Objekt-Formular...</p>;

  return (
    <>
      <Navbar />
      <ObjektDetailForm
        formData={formData}
        handleChange={handleChange}
        onImageDelete={handleImageDelete}
        onImageChange={handleImageChange}
        onSave={updateObjekt}
        onDelete={handleDelete}
        onCancel={editMode ? handleCancelEdit : () => navigate("/objekte")}
        editMode={editMode}
        setEditMode={setEditMode}
        kategorienData={kategorien}
        ablageorteData={ablageorte}
        typenData={typen}
        laenderData={laender}
        materialienData={materialien}
        waehrungenData={waehrungen}
        zustaendeData={zustaende}
        eigentuemerData={eigentuemer}
        selectedFiles={selectedFiles}
        errorMessage={errorMessage}
      />
    </>
  );
};

export default ObjektDetail;
